// src/components/ui/AIStatusIndicatorBar.js
import React, { useEffect, useMemo, useState } from 'react';
import LoadingState from './LoadingState';
import ListeningState from './ListeningState';
import SpeakingState from './SpeakingState';
import IdleState from './IdleState';
import './aiStatusMini.css'; // estilos pequeños para “en barra”

export default function AIStatusIndicatorBar({
  status = 'idle',
  muted = false,
  onMicPress,
  className = '',
  style = {},
}) {
  const [micLevel, setMicLevel] = useState(0);
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(orientation: landscape)').matches
      : false
  );

  useEffect(() => {
    if (!window.matchMedia) return;
    const mm = window.matchMedia('(orientation: landscape)');
    const onChange = () => setIsLandscape(mm.matches);
    if (mm.addEventListener) mm.addEventListener('change', onChange);
    else mm.addListener(onChange);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener('change', onChange);
      else mm.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      if (window.gptRealtimeControl?.getMicLevel && !muted) {
        setMicLevel(window.gptRealtimeControl.getMicLevel());
      } else {
        setMicLevel(0);
      }
    }, 100);
    return () => clearInterval(id);
  }, [muted]);

  const StateComponent = useMemo(() => {
    switch (status) {
      case 'loading':   return LoadingState;
      case 'listening': return ListeningState;
      case 'speaking':  return SpeakingState;
      case 'idle':
      default:          return IdleState;
    }
  }, [status]);

  const intensity = muted || micLevel < 5 ? 0 : Math.min(100, micLevel);

  // ---- escalas base por orientación ----
  const baseScale = isLandscape ? 0.7 : 0.8;        
  const ringBase  = isLandscape ? 0.7 : 1;      
  const micScale  = baseScale * (1 + (intensity / 100) * 0.2);

  const children = [];

  // zona del círculo blanco + ondas
  children.push(
    React.createElement(
      'div',
      { key: 'visual', className: 'ai-visual-area ai-bar-visual' },
      React.createElement(StateComponent, { muted })
    )
  );

  // botón mic + anillos de pulso
  const micRings = [];
  if (!muted && intensity > 15) {
    // tamaños de anillos ajustados por orientación
    const sizes = [
      { w: 60 * ringBase,  h: 60 * ringBase,  key: 'r1',
        opacity: Math.min(0.8, intensity / 50),
        dur: 1.2 - (intensity / 100) * 0.4
      },
      { w: 80 * ringBase,  h: 80 * ringBase,  key: 'r2',
        opacity: Math.min(0.6, (intensity - 20) / 60),
        dur: 1.0 - (intensity / 100) * 0.3,
        show: intensity > 40
      },
      { w: 100 * ringBase, h: 100 * ringBase, key: 'r3',
        opacity: Math.min(0.4, (intensity - 50) / 50),
        dur: 0.8 - (intensity / 100) * 0.2,
        show: intensity > 70
      },
    ];

    sizes.forEach((s, idx) => {
      if (s.show === false) return;
      micRings.push(
        React.createElement('div', {
          key: s.key,
          className: `ai-mic-pulse-ring ai-mic-ring-${idx + 1}`,
          style: {
            width: `${s.w}px`,
            height: `${s.h}px`,
            opacity: s.opacity,
            animationDuration: `${Math.max(0.4, s.dur)}s`,
          },
        })
      );
    });
  }

  children.push(
    React.createElement(
      'button',
      {
        key: 'mic',
        type: 'button',
        onClick: onMicPress,
        className: `ai-mic-btn ${intensity > 10 ? 'ai-mic-active' : ''}`,
        'aria-label': muted ? 'Activar micrófono' : 'Desactivar micrófono',
        style: {
          transform: `scale(${micScale})`, // <- usa la escala base según orientación
          filter: `drop-shadow(0 0 ${4 + intensity * 0.15}px rgba(34, 197, 94, ${intensity / 200}))`,
          transition: 'transform 0.1s ease-out, filter 0.1s ease-out',
          transformOrigin: 'center',
        },
      },
      React.createElement('img', {
        src: muted ? '/assets/micro-offf_chat.png' : '/assets/micro-home.png',
        alt: muted ? 'Mic apagado' : 'Mic encendido',
        className: 'ai-mic-icon',
        style: {
          filter: `brightness(${1 + (intensity / 100) * 0.3})`,
          transition: 'filter 0.1s ease-out',
        },
      }),
      ...micRings
    )
  );

  return React.createElement(
    'div',
    { className: `ai-status-container ai-inbar ${className}`, style },
    ...children
  );
}
